import { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuthContext } from '../contexts/AuthContext';
import { AkorepsWordmark } from '../components/AkorepsWordmark';
import api from '../lib/api';
import { acceptPendingInvite } from '../lib/pendingInvite';

const TERMS_URL = import.meta.env.VITE_TERMS_URL || 'https://akoreps.com/terms';
const PRIVACY_URL = import.meta.env.VITE_PRIVACY_URL || 'https://akoreps.com/privacy';

export function AcceptTermsPage() {
  const [searchParams] = useSearchParams();
  const continuationToken = searchParams.get('continuation');
  const { login } = useAuthContext();
  const navigate = useNavigate();
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptPrivacy, setAcceptPrivacy] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!continuationToken) {
      setError('Missing sign-in session. Please start again from the login page.');
      return;
    }
    if (!acceptTerms || !acceptPrivacy) {
      setError('Please accept both the Terms of Service and the Privacy Policy to continue.');
      return;
    }
    setSubmitting(true);
    try {
      const response = await api.post<{ token?: string }>('/auth/accept-terms', {
        continuationToken,
        acceptTerms: true,
        acceptPrivacy: true,
      });
      let accessToken = response.data?.token;
      if (!accessToken || typeof accessToken !== 'string') {
        setError('Something went wrong. Please try again.');
        return;
      }

      const upgraded = await acceptPendingInvite(accessToken);
      if (upgraded) accessToken = upgraded;

      login(accessToken);
      navigate('/', { replace: true });
    } catch (err: unknown) {
      if (axios.isAxiosError<{ error?: string }>(err)) {
        setError(err.response?.data?.error || 'Could not complete sign-up');
        return;
      }
      setError('Could not complete sign-up');
    } finally {
      setSubmitting(false);
    }
  };

  if (!continuationToken) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md w-full bg-white p-8 rounded-lg shadow-sm border text-center">
          <h2 className="text-xl font-bold text-gray-900 mb-2">Session expired</h2>
          <p className="text-gray-600 mb-6">
            This link is invalid or expired. Please sign in again from the login page.
          </p>
          <Link
            to="/login"
            className="inline-block bg-green-700 text-white py-2 px-4 rounded-md font-medium hover:bg-green-800 transition-colors"
          >
            Back to login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4 py-12">
      <div className="max-w-md w-full bg-white p-8 rounded-lg shadow-sm border">
        <div className="text-center mb-6">
          <h1 className="flex justify-center">
            <span className="sr-only">Akoreps</span>
            <AkorepsWordmark className="h-8 w-auto" aria-hidden />
          </h1>
          <p className="text-gray-600 mt-2 text-sm">Almost there — review and accept to continue</p>
        </div>
        <p className="text-sm text-gray-600 mb-6">
          Before we create your account, please confirm you have read and agree to our legal
          documents.
        </p>
        <ul className="text-sm text-gray-700 mb-6 space-y-2">
          <li>
            <a
              href={TERMS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="text-green-700 font-medium hover:text-green-900 underline"
            >
              Terms of Service
            </a>
          </li>
          <li>
            <a
              href={PRIVACY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="text-green-700 font-medium hover:text-green-900 underline"
            >
              Privacy Policy
            </a>
          </li>
        </ul>
        <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
          <label className="flex items-start gap-3 cursor-pointer text-sm text-gray-800">
            <input
              type="checkbox"
              checked={acceptTerms}
              onChange={(e) => setAcceptTerms(e.target.checked)}
              className="mt-1 rounded border-gray-300 text-green-700 focus:ring-green-500"
            />
            <span>
              I have read and agree to the{' '}
              <a
                href={TERMS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="text-green-700 font-medium hover:text-green-900 underline"
                onClick={(e) => e.stopPropagation()}
              >
                Terms of Service
              </a>
              .
            </span>
          </label>
          <label className="flex items-start gap-3 cursor-pointer text-sm text-gray-800">
            <input
              type="checkbox"
              checked={acceptPrivacy}
              onChange={(e) => setAcceptPrivacy(e.target.checked)}
              className="mt-1 rounded border-gray-300 text-green-700 focus:ring-green-500"
            />
            <span>
              I have read and agree to the{' '}
              <a
                href={PRIVACY_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="text-green-700 font-medium hover:text-green-900 underline"
                onClick={(e) => e.stopPropagation()}
              >
                Privacy Policy
              </a>
              .
            </span>
          </label>
          {error && <p className="text-sm text-red-600">{error}</p>}
          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-green-700 text-white py-2 px-4 rounded-md font-medium hover:bg-green-800 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {submitting ? 'Creating your account…' : 'Continue'}
          </button>
        </form>
        <p className="mt-4 text-center text-sm text-gray-500">
          <Link to="/login" className="text-green-700 hover:text-green-900 font-medium">
            Cancel and return to login
          </Link>
        </p>
      </div>
    </div>
  );
}
